import { useEffect, useMemo, useState } from 'react';
import { Trophy } from 'lucide-react';
import { api } from '../api';
import './PRCard.css';

function fmtWeight(n) {
  if (n == null) return '—';
  const num = Number(n);
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
}

export default function PRCard({
  exerciseId,
  exerciseName = 'Personal record',
  unit = 'kg',
  from,              // optional YYYY-MM-DD
  to,                // optional YYYY-MM-DD
  className = '',
  style = {}
}) {
  const [pr, setPr] = useState(null);  // null during first load; object afterwards

  const rangeLabel = useMemo(() => {
    if (from && to)  return 'Range';
    if (from)        return 'Since';
    if (to)          return 'Until';
    return '';
  }, [from, to]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to)   qs.set('to', to);
        const url = `/me/analytics/max-weight/${exerciseId}${qs.size ? `?${qs}` : ''}`;
        const res = await api(url);
        if (!alive) return;
        setPr(res?.ok ? res : { ok: true, maxWeight: 0, ymd: null, reps: null });
      } catch {
        if (alive) setPr({ ok: true, maxWeight: 0, ymd: null, reps: null });
      }
    })();
    return () => { alive = false; };
  }, [exerciseId, from, to]);

  const hasPR = pr && Number(pr.maxWeight) > 0;

  return (
    <div className={`panel pr-card has-glow glow-center ${className}`} style={style}>
      <header className="pr-top">
        <div className="icon-badge" title="Personal Record">
          <Trophy size={18} />
        </div>
        <div className="pr-title" title={exerciseName}>{exerciseName}</div>
        <div className="pr-range">{rangeLabel}</div>
      </header>

      <div className="pr-center">
        {pr === null ? (
          <div className="skeleton-num" />
        ) : (
          <div className="pr-value" aria-live="polite">
            <span className="num">{fmtWeight(pr.maxWeight)}</span>
            <span className="unit">{hasPR ? unit : ''}</span>
          </div>
        )}
      </div>

      <footer className="pr-meta">
        {hasPR ? (
          <>
            <span className="date">on {pr.ymd}</span>
            {pr.reps ? <span className="dot">·</span> : null}
            {pr.reps ? <span className="reps">{pr.reps} reps</span> : null}
          </>
        ) : (
          <span className="muted">No PR yet</span>
        )}
      </footer>
    </div>
  );
}